import Button from '@atoms/Button/Button';
import { FilterCollection, FilterEvent } from '@hooks/useFilters';
import useTranslations from '@hooks/useTranslations';
import DateRangePicker from '@molecules/DateRangePicker/DateRangePicker';
import SearchInput from '@molecules/SearchInput/SearchInput';
import { isRangeFilter } from '@type-declarations/filter';
import { FilterGroup } from '@type-declarations/filters';
import { Tag } from '@type-declarations/prepr';
import { isActiveDateFilter } from '@utils/filters/dateFilter';
import { isActiveDistanceFilter } from '@utils/filters/distanceFilter';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { Fragment, useState } from 'react';

import Fieldset from './Fieldset';
import FilterButton from './FilterButton';
import FilterModal from './FilterModal';
import styles from './Filters.module.scss';

const MODAL_ID = 'filter-modal';

interface Props {
  highlightedTag?: Tag;
  primary: FilterGroup[];
  secondary: FilterGroup[];
  activeFilters: FilterCollection;
  searchLabel?: string;
  hasDateFilter?: boolean;
  hideSubSubCategories?: boolean;
  onChange: (event: FilterEvent) => void;
  onClear: () => void;
  modifier?: 'searchFilters';
}

export default function Filters({
  highlightedTag,
  primary,
  secondary,
  activeFilters,
  searchLabel,
  hasDateFilter = false,
  hideSubSubCategories = false,
  onChange,
  onClear,
  modifier,
}: Props) {
  const { query } = useRouter();
  const t = useTranslations();

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const isActive = (groupKey: keyof FilterCollection, value: string) => {
    const group = activeFilters[groupKey];

    if (group === undefined || group === null) return false;

    if (typeof group === 'boolean') {
      return group;
    }

    if (typeof group === 'string') {
      return group === value;
    }

    if (isRangeFilter(group)) {
      if (groupKey === 'date') {
        return isActiveDateFilter(group, value);
      }

      if (groupKey === 'distance') {
        return isActiveDistanceFilter(group, value);
      }

      return false;
    }

    return group.includes(value);
  };

  return (
    <>
      <div className={clsx(styles.container, modifier && styles[modifier])}>
        {highlightedTag && (
          <FilterButton
            iconLeft={highlightedTag.icon}
            filter={{ value: highlightedTag.slug, title: highlightedTag.title }}
            active={isActive('tags', highlightedTag.slug)}
            group={{ key: 'tags' }}
            onChange={onChange}
            theme={highlightedTag.theme}
          />
        )}

        {primary.map(group => (
          <Fragment key={`primary-group-${group.key}`}>
            {group.filters.map(filter => {
              const active = isActive(group.key, filter.value);

              return (
                <FilterButton
                  filter={filter}
                  active={active}
                  group={group}
                  onChange={onChange}
                  key={`primary-filter-${filter.value}`}
                />
              );
            })}
          </Fragment>
        ))}

        {hasDateFilter && (
          <DateRangePicker
            onChange={dates => onChange({ type: 'date', value: dates })}
            className={styles.datePicker}
            initialRange={activeFilters.date}
          />
        )}

        <div className={styles.searchFilterContainer}>
          {searchLabel && (
            <SearchInput
              name="title"
              label={searchLabel}
              defaultValue={query.title}
              icon
              onChange={value => onChange({ type: 'title', value })}
            />
          )}

          {!!secondary.length && (
            <Button
              label={t.filter}
              iconLeft="FILTER"
              icon="NAV_ARROW_RIGHT"
              onClick={() => setModalIsOpen(true)}
              aria-controls={MODAL_ID}
              aria-expanded={modalIsOpen}
              count={
                (activeFilters.tags?.length || 0) +
                (activeFilters.accessibility?.length || 0) +
                (activeFilters.categories?.length || 0)
              }
            />
          )}
        </div>
      </div>
      {!!secondary.length && (
        <FilterModal
          id={MODAL_ID}
          isOpen={modalIsOpen}
          onClose={() => setModalIsOpen(false)}
          onClear={onClear}
        >
          {secondary.map((group, i) => (
            <Fieldset
              // eslint-disable-next-line react/no-array-index-key
              key={`${group.key}-${i}`}
              group={group}
              isActive={isActive}
              onChange={onChange}
              activeFilters={activeFilters}
              hideSubSubCategories={hideSubSubCategories}
            />
          ))}
        </FilterModal>
      )}
    </>
  );
}
